import {
    useEffect, useState, useMemo, useCallback,
} from 'react'
import isEmpty from 'lodash/isEmpty'
import Typography from '@mui/material/Typography'

import {
    WithId,
} from 'app/types/common.types'
import StatusHandler from 'app/shared-components/StatusHandler'
import useGetServiceCenterById from '../hooks/useGetServiceCenterById'
import PreBookings from '../components/PreBookings'
import {
    ScenarioEditorDataType,
} from './ScenarioEditor.types'
import ScenarioEditorForm from './ScenarioEditorForm'
import {
    ScenarioEditorContext,
} from './ScenarioEditorContext'
import ForecastsByProduct from './ForecastsByProduct'
import ScenarioEditorAvailabilityChart from './ScenarioEditorAvailabilityChart'
import OutboundOrder from './OutboundOrder'
import useStyles from './ScenarioEditor.styles'
import InboundOrders from './InboundOrders'
import ContainersDamaged from './ContainersDamaged'
import ContainerStatusBreakdown from './ContainerStatusBreakdown'

export default function ScenarioEditor({
    id,
}: Readonly<WithId<number>>) {
    const {
        classes,
    } = useStyles()

    const [
        value,
        setValue,
    ] = useState<ScenarioEditorDataType>()

    const {
        data,
        isDataReady,
        isError,
        error,
        isFetching,
    } = useGetServiceCenterById(id)

    useEffect(() => {
        if (isDataReady && data?.id !== value?.serviceCenter?.id) {
            setValue({
                orderSelectionDelay: data?.orderSelectionDelay ?? 0,
                serviceCenter: {
                    id: data?.id,
                },
                includeNotStartedOrders: false,
                includePreBookings: false,
                productType: 'ALL',
                forecastsByProduct: [],
            })
        }
    }, [
        data,
        isDataReady,
        value?.serviceCenter?.id,
    ])

    const setContextValue = useCallback((newValue: Partial<ScenarioEditorDataType>) => {
        setValue({
            ...value,
            ...newValue,
        })
    }, [value])

    const context = useMemo(() => {
        return {
            contextValue: value,
            setContextValue,
        }
    }, [
        value,
        setContextValue,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady && !isEmpty(value)}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <ScenarioEditorContext.Provider value={context}>
                <ScenarioEditorForm />
                <ForecastsByProduct />
                <ScenarioEditorAvailabilityChart />
                <Typography
                    variant="h2"
                    className={classes.title}
                >
                    Support Information
                </Typography>
                <ContainerStatusBreakdown serviceCenterId={id} />
                <OutboundOrder serviceCenterId={id} />
                <PreBookings serviceCenterId={id} />
                <InboundOrders serviceCenterId={id} />
                <ContainersDamaged serviceCenterId={id} />
            </ScenarioEditorContext.Provider>
        </StatusHandler>
    )
}
